import React, {
  useState,
  useEffect,
  useImperativeHandle,
  useRef,
  RefObject,
} from 'react'
import { useSelector, useDispatch } from 'react-redux'

import { addFlashMessage } from '~/actions/flash-message'
import DoubleBounceSpinner from '~/components/double-bounce-spinner'
import LoadingModal from '~/components/loading-modal'
import { FREEDOMPAY_HPC_URL } from '~/config'
import { useFreedomPayHpcSessionKey } from '~/contexts/freedom-pay-hpc-session-key'
import { i18n } from '~/i18n'
import getCustomerAPI from '~/utils/api/get-customer-api'
import { getErrorMessage } from '~/utils/credit-card'

import FreedomPayHpc from './freedom-pay-hpc.component'

import type { AppState } from '~/reducers'

type Props = {
  onSuccess: () => any
  saveCardOnFile: boolean
  setCartCreditCard?: boolean
  onChangeSaveCardOnFile?: () => void
  hpcReference: RefObject<unknown>
}

export default function FreedomPayHpcContainer({
  onSuccess,
  saveCardOnFile,
  onChangeSaveCardOnFile,
  setCartCreditCard,
  hpcReference,
}: Props) {
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const cart = useSelector((state: AppState) => state.cart)
  const [sessionKey, , reloadSessionKey] = useFreedomPayHpcSessionKey()

  const dispatch = useDispatch()
  const api = getCustomerAPI()
  const [frameHeight, setFrameHeight] = useState(400)
  const [loading, setLoading] = useState(false)
  const [processing, setProcessing] = useState(false)
  const [formState, setFormState] = useState<{
    nameOnCard: string
  }>({
    nameOnCard: '',
  })
  const [errors, setErrors] = useState({})
  const [noErrorHeight, setNoErrorHeight] = useState(null)

  useImperativeHandle(
    hpcReference,
    () => ({
      submit() {
        if (!formState.nameOnCard) {
          setErrors({
            ...errors,
            nameOnCard: i18n.t(
              'pages.payment.credit_card.name_on_card.required',
            ),
          })
          return
        }

        setErrors({})
        setProcessing(true)

        iframeRef.current?.contentWindow?.postMessage(
          'getPaymentKeys',
          new URL(FREEDOMPAY_HPC_URL).origin,
        )
      },
    }),
    [formState],
  )

  async function paymentKeyCallback(paymentKey) {
    setLoading(true)

    try {
      if (!sessionKey) throw 'no sessionKey, unable to proceed'

      const requestParams = {
        sessionKey,
        paymentKey,
        savedOnFileAt: saveCardOnFile ? new Date().toISOString() : undefined,
        setCartCreditCard: !!setCartCreditCard,
        nameOnCard: formState.nameOnCard,
      }

      const response = await api.createFreedomPayHpcCreditCard(requestParams)
      reloadSessionKey()

      if (response.ok) {
        setLoading(false)
        onSuccess()
      } else {
        const { errors } = await response.json()
        setLoading(false)
        setErrors({
          base: getErrorMessage(errors),
        })
        setProcessing(false)
      }
    } catch {
      setLoading(false)
      dispatch(
        addFlashMessage(
          'error',
          i18n.t('pages.payment.credit_card.tokenization_failed'),
        ),
      )
      setProcessing(false)
    }
  }

  useEffect(() => {
    function messageHandler(e) {
      const message = e.data
      const data = message.data

      switch (message.type) {
        case 1:
          dispatch(
            addFlashMessage(
              'error',
              i18n.t('components.flash_messages.freedompay_hpc_error'),
            ),
          )
          setProcessing(false)
          reloadSessionKey()
          break

        case 2:
          setFrameHeight(data.height)

          // Oh, the joys of working with FreedomPay...
          // Unfortunately, it is very difficult to know if there is a validation error
          // in the HPC iframe. If we submit the form and there is a validation error, we
          // want to hide the loading modal. FreedomPay does not call the payment key
          // callback in this case, and we end up spinning forever.
          //
          // FreedomPay sends events to tell us to change the height of the iframe. When
          // the iframe loads, there are no error messages. If there are error messages
          // then FreedomPay will tell us to set the height to a bigger number. This is
          // how we determine if there were validation errors in the HPC form.
          if (noErrorHeight === null) {
            setNoErrorHeight(data.height)
          } else if (data.height > noErrorHeight) {
            setProcessing(false)
          }

          break

        case 3:
          paymentKeyCallback(data.paymentKeys[0])
          break

        default:
          break
      }
    }

    window.addEventListener('message', messageHandler)
    return () => window.removeEventListener('message', messageHandler)
  }, [sessionKey, formState, saveCardOnFile])

  if (!sessionKey || loading) {
    return (
      <>
        <DoubleBounceSpinner />
        {processing && <LoadingModal />}
      </>
    )
  }

  return (
    <>
      <FreedomPayHpc
        key={sessionKey}
        cart={cart}
        frameHeight={frameHeight}
        sessionKey={sessionKey}
        iframeRef={iframeRef}
        formState={formState}
        onChangeSaveCardOnFile={onChangeSaveCardOnFile}
        saveCardOnFile={saveCardOnFile}
        onChange={(field, value) => {
          setFormState({ ...formState, [field]: value })
          setErrors({ ...errors, [field]: undefined })
        }}
        errors={errors}
      />
      {processing && <LoadingModal />}
    </>
  )
}
